/**
 * @module app.um
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable, observable } from 'aurelia-typed-observable-plugin';
import { AuthService } from 'app/um/services/auth-service';
import { LookupService } from 'app/core/services/lookup-service';
import { apiCall } from 'app/shared/lib/api/api';
import { ApiDecimal } from 'app/shared/models/essette-api';
import { getReference } from 'app/shared/models/essette-types';
import { Toaster } from 'app/shared/lib/toaster';
import { deepClone, isNullOrUndefined } from 'app/shared/lib/utilities';
import { ActionOptions, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { DateComparison } from 'app/shared/lib/date-utilities';
import { FormatProvider } from 'app/shared/lib/formatting/format-provider';
import { IAuthContextSettingsDetails } from 'app/um/lib/auth-context';
import { IMedicationChange } from 'app/pp/components/update/update-auth-details-step';
import * as UM from 'app/um/models/essette-api';
import * as Core from 'app/core/models/essette-api';

export interface ISaveAuthMedicationOptions {
    showStatusReason: boolean;
    showQuantityApproved: boolean;
    showUnitApproved: boolean;
    showDaysApproved: boolean;
    showApprovedCharges: boolean;
    portalEditMode?: boolean;
    pendMedicationChanges?: boolean;
}

export const defaultOptions: ISaveAuthMedicationOptions = {
    showStatusReason: true,
    showQuantityApproved: true,
    showUnitApproved: true,
    showDaysApproved: true,
    showApprovedCharges: true,
    portalEditMode: false,
    pendMedicationChanges: false
}

@autoinject()
export class SaveAuthMedication extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ changeHandler: 'medicationChanged' })
    public medication: UM.AuthMedication | null = null;

    @bindable.objectNull({ defaultBindingMode: bindingMode.twoWay })
    public auth: UM.Auth | null = null;

    @bindable.object({ defaultBindingMode: bindingMode.oneTime })
    public options: ISaveAuthMedicationOptions = defaultOptions;

    @bindable.object()
    public drugApprovedCharges: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugRequestedCharges: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugRequestedDays: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugRequestedQuantity: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugsAuthDate: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugsExpDate: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugsStatusReason: IAuthContextSettingsDetails = {};

    @bindable.array()
    protected allTransitions: UM.AuthMedicationStatus[] = [];

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public addedFromPortalEditMode: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    protected pendingMedicationChanges: IMedicationChange[] = [];

    @observable
    protected requestedCharge: string | null = null;

    protected selectedStatus: UM.AuthMedicationStatus | null = null;
    protected selectedStatusReason: UM.AuthMedicationStatusReason | null = null;
    protected transitions: UM.AuthMedicationStatus[] = [];
    protected showDates: boolean = false;
    protected hasValidDates: boolean = true;
    protected selectedDays: UM.AuthDetailQuantityContainer = {}

    protected requestedUnitSearchQuery: string | null = null;
    private requestedUnitSearchResults: Core.UnitOfMeasure[] = [];
    protected approvedUnitSearchQuery: string | null = null;
    private approvedUnitSearchResults: Core.UnitOfMeasure[] = [];

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        private readonly toaster: Toaster,
        private readonly formatProvider: FormatProvider,
        private readonly lookupService: LookupService,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(!this.medication) { throw new Error('Bindable property [medication] must be bound and include an id'); }
        if(!this.auth?.id) { throw new Error('Bindable property [auth] was not set'); }
        this.medicationChanged();

        this.showDates = this.drugsAuthDate.visible === true || this.drugsExpDate.visible === true;
        if(this.showDates) {
            this.dateChanged();
        }
    }

    ////****************** Events ***************************************************////

    //update the approved quantity when requested quantity is changed, if the status reason is set to approved
    protected quantityRequestedChanged(requestedQuantity: ApiDecimal | null): void {
        if(this.medication) {
            if(this.selectedStatus?.determination?.status?.determination?.result === 'approved') {
                this.medication.quantity = {
                    approved: { value: requestedQuantity, unit: this.medication.quantity?.approved?.unit },
                    requested: { value: requestedQuantity, unit: this.medication.quantity?.requested?.unit },
                };
            }
        }
    }

    //if status is already selected and is set to approved or (is modified and approved days is not already set), then set approved days to be the same as requested days
    protected daysRequestedChanged(requestedDays: number | undefined): void {
        if(this.medication) {
            const result = this.selectedStatus?.determination?.status?.determination?.result;
            if(result === 'approved' || (result === 'modified' && this.selectedDays.approved === null)) {
                this.selectedDays = {
                    approved: requestedDays,
                    requested: requestedDays,
                }
            } else {
                this.selectedDays = { ...this.selectedDays, requested: requestedDays }
            }
        }
    }

    /** Sets form input values to values on the medication object */
    protected medicationChanged(): void {
        this.selectedStatusReason = this.medication?.status?.reason || null;
        this.selectedStatus = this.medication?.status || null;
        this.configureTransitions();

        if(!!this.medication?.charge?.requested) {
            this.requestedCharge = this.formatProvider.formatDecimal(Number.parseFloat(this.medication.charge.requested), { format: 'fixed2' });
        } else {
            this.requestedCharge = null;
        }

        if(!!this.medication?.charge?.approved) {
            this.medication.charge.approved = this.formatProvider.formatDecimal(Number.parseFloat(this.medication.charge.approved), { format: 'fixed2' });
        }

        if(!this.medication?.authorizedDate && this.medication) {
            this.medication.authorizedDate = this.auth?.serviceDate?.dateRange?.start;
        }

        if(!this.medication?.expirationDate && this.medication) {
            this.medication.expirationDate = this.auth?.serviceDate?.dateRange?.end;
        }

        this.selectedDays = {};
        if(!isNullOrUndefined(this.medication?.days?.approved)) {
            this.selectedDays.approved = this.medication.days.approved;
        }
        if(!isNullOrUndefined(this.medication?.days?.requested)) {
            this.selectedDays.requested = this.medication.days.requested;
        }

        this.dateChanged();

        this.requestedUnitSearchQuery = null;
        this.requestedUnitSearchResults.splice(0);
        this.approvedUnitSearchQuery = null;
        this.approvedUnitSearchResults.splice(0);
    }

    protected statusChanged(status: UM.AuthMedicationStatus): void {
        let quantity = this.medication?.quantity;
        const charge = this.medication?.charge;
        const days = this.medication?.days;
        if(!quantity) {
            quantity = {
                requested: { value: "1.00" as ApiDecimal },
                approved: { value: undefined }
            }
        }
        if(!status) {
            quantity.approved = { value: "0.00" };
            if(!!charge && this.drugApprovedCharges.visible) {
                charge.approved = null;
            }
            if(!!this.selectedDays && this.options.showDaysApproved) {
                this.selectedDays.approved = null;
            }
            return;
        }

        // if no reasons or reason not found for selected status, clear out previously selected reason
        if(!this.selectedStatus?.reasons?.length || !this.selectedStatus.reasons.find(r => r.id === this.selectedStatusReason?.id)) {
            this.selectedStatusReason = null;
        }

        this.dateChanged();

        const { result } = status.determination?.status?.determination ?? { result: 'none' };
        switch(result) {
            case 'modified':
                quantity.approved!.value = this.medication?.quantity?.requested?.value;
                quantity.approved!.unit = this.medication?.quantity?.requested?.unit;

                if(!!days && this.options.showDaysApproved) this.selectedDays.approved = days.requested;
                break;
            case 'approved':
                quantity.approved!.value = this.medication?.quantity?.requested?.value;
                quantity.approved!.unit = this.medication?.quantity?.requested?.unit;

                if(!!charge && this.drugApprovedCharges.visible) {
                    charge.approved = this.requestedCharge ? this.formatProvider.formatDecimal(Number.parseFloat(this.requestedCharge), { format: 'fixed2' }) : null;
                }
                if(!!days && this.options.showDaysApproved) this.selectedDays.approved = days.requested;
                break;
            case 'other':
            case 'denied':
                quantity.approved = { value: "0", unit: undefined };

                if(!!charge && this.drugApprovedCharges.visible) charge.approved = "0.00";
                if(!!days && this.options.showDaysApproved) this.selectedDays.approved = 0;
                break;
            default:
                quantity.approved = { value: "0", unit: undefined };

                if(!!charge && this.drugApprovedCharges.visible) charge.approved = null;
                if(!!days && this.options.showDaysApproved) this.selectedDays.approved = null;
                break;
        }
    }

    private dateChanged(): void {
        const drugValidStart = this.medication?.definition?.dateRange?.start || null;
        const drugValidEnd = this.medication?.definition?.dateRange?.end || null;
        const authorizedDate = this.medication?.authorizedDate || null;
        const expirationDate = this.medication?.expirationDate || null;
        const allowNullDateOfService = this.selectedStatus?.flag?.allowNullDateOfService || false;

        //Dates are ALWAYS valid when allowNullDates are true and they pass the checkForContainingDates.
        if((this.drugsAuthDate.reqDet || this.drugsAuthDate.reqDataEntry) && !authorizedDate && !allowNullDateOfService) {
            this.hasValidDates = false
        } else if((this.drugsExpDate.reqDet || this.drugsExpDate.reqDataEntry) && !expirationDate && !allowNullDateOfService) {
            this.hasValidDates = false
        } else if(!DateComparison.checkForContainingDates(drugValidStart, drugValidEnd, authorizedDate || drugValidStart, expirationDate || drugValidEnd)) {
            this.hasValidDates = false
        } else {
            this.hasValidDates = true
        }
    }

    protected requestedChargeChanged(newValue: number): void {
        if(!!this.selectedStatus?.determination?.status?.determination) {
            switch(this.selectedStatus.determination?.status.determination.result) {
                case 'modified':
                    break;
                case 'approved':
                    if(!!this.medication?.charge && this.drugApprovedCharges?.visible) {
                        this.medication.charge.approved = this.requestedCharge?.toString()
                    }
                    break;
                case 'other':
                case 'denied':
                    if(!!this.medication?.charge && this.drugApprovedCharges?.visible) {
                        this.medication.charge.approved = this.requestedCharge != null ? "0.00" : null;
                    }
                    break;
            }
        }

        if(!!this.medication?.charge) {
            this.medication.charge.requested = newValue?.toString()
        }
    }

    protected requestedUnitChanged(unit: Core.UnitOfMeasure): void {
        if(!this.medication) return;
        this.forceDetailQuantity();

        //if Status is already selected and is set to approved or is modified and approved unit is not already set, then set approved unit to be the same as requested unit
        const result = this.selectedStatus?.determination?.status?.determination?.result;
        if(result === 'approved' || (result === 'modified' && this.medication?.quantity?.approved?.unit === null)) {
            this.medication!.quantity!.approved!.unit = unit;
            this.medication!.quantity!.approved!.value = this.medication!.quantity!.requested?.value;
        }

        if(!this.medication?.quantity?.requested) {
            this.medication.quantity!.requested = { unit: undefined };
        };
        this.requestedUnitSearchQuery = null;
        // clear search results (and thus hide the auto-complete result list)
        this.requestedUnitSearchResults.splice(0);
    }

    protected async selectApprovedUnit(unit: Core.UnitOfMeasure): Promise<void> {
        if(!this.medication) return;
        this.forceDetailQuantity();

        if(!this.medication?.quantity?.approved) {
            this.medication.quantity!.approved = { unit: undefined };
        };
        this.medication!.quantity!.approved.unit = unit;
        this.approvedUnitSearchQuery = null;
        // clear search results (and thus hide the auto-complete result list)
        this.approvedUnitSearchResults.splice(0);
    }

    ////****************** Functions ************************************************////
    protected configureTransitions(): void {
        // medication should only be able to change status to those among the current status's transitions
        if(!this.medication?.status?.transitions) {
            // if there are no transitions, use all transitions
            this.transitions = deepClone(this.allTransitions);
            return;
        }
        this.transitions = deepClone(this.medication.status.transitions);
        // include the current status among the list of transitions so that dropdown has an initial value
        const currentStatus = this.medication.status;
        const currentStatusFound = this.transitions.some(status => status.id === currentStatus.id);
        if(currentStatusFound) return;
        this.transitions.push(currentStatus);
        this.transitions.sort((a, b) => {
            if(!a.description || !b.description) return 0;
            if(a.description < b.description) return -1;
            if(a.description > b.description) return 1;
            return 0;
        });
    }

    private forceDetailQuantity() {
        if(!this.medication) this.medication = { quantity: undefined };
    }

    protected async searchRequestedUnits(value: string) {
        if(!value) {
            if(this.requestedUnitSearchResults) this.requestedUnitSearchResults = [];
            return;
        }
        this.requestedUnitSearchResults = await this.searchUnits(value);
    }

    protected async searchApprovedUnits(value: string) {
        if(!value) {
            if(this.approvedUnitSearchResults) this.approvedUnitSearchResults = [];
            return;
        }
        this.approvedUnitSearchResults = await this.searchUnits(value);
    }

    protected clearRequestedUnit(): void {
        this.medication!.quantity!.requested!.unit = null;
        this.requestedUnitSearchQuery = null;
        this.requestedUnitSearchResults.splice(0);
    }

    protected clearApprovedUnit(): void {
        this.medication!.quantity!.approved!.unit = null;
        this.approvedUnitSearchQuery = null;
        this.approvedUnitSearchResults.splice(0);
    }

    private buildSaveObject(): UM.AuthMedication {
        if(!this.medication) throw new Error('Missing medication object for save');
        if(!this.medication.quantity) throw new Error('Missing medication quantity for save');

        const medication: UM.AuthMedication = {
            id: this.medication.id,
            status: getReference(this.selectedStatus),
            quantity: {
                approved: {},
                requested: {},
            },
            charge: {
                approved: this.medication.charge?.approved || null,
                requested: this.requestedCharge
            },
            authorizedDate: this.medication.authorizedDate || null,
            expirationDate: this.medication.expirationDate || null,
            days: this.selectedDays,
            sequence: this.medication.sequence
        };

        if(this.options.pendMedicationChanges) {
            medication.definition = {
                code: this.medication.definition?.code,
                description: this.medication.definition?.description
            };
        }

        if(this.medication?.quantity?.requested?.value != null) {
            const requestedUnitOfMeasureId = this.medication.quantity.requested.unit?.id;
            const requestedUnitOfMeasureCode = this.medication.quantity.requested.unit?.code;
            medication.quantity!.requested = {
                value: this.medication.quantity.requested.value,
                unit: { id: requestedUnitOfMeasureId },
                display: this.options.pendMedicationChanges ? (this.medication.quantity.requested.value + (!!requestedUnitOfMeasureCode ? (' ' + requestedUnitOfMeasureCode) : '')) : null
            };
        }
        if(this.options.showQuantityApproved && this.medication.quantity?.approved?.value != null) {
            medication.quantity!.approved = {
                value: this.medication.quantity.approved.value,
                unit: {
                    id: this.medication.quantity.approved.unit
                        ? this.medication.quantity.approved.unit.id
                        : this.medication.quantity.requested?.unit?.id
                }
            };
        }
        if(medication.status) {
            const statusReason = getReference(this.selectedStatusReason);
            medication.status.reason = statusReason ?? null;
        }

        return medication;
    }

    ////****************** IForm ****************************************************////

    public reset(): void {
        this.medication = null;
        this.selectedStatus = null;
        this.selectedStatusReason = null;
        this.requestedCharge = null;
        this.requestedUnitSearchQuery = null;
        this.requestedUnitSearchResults.splice(0);
        this.approvedUnitSearchQuery = null;
        this.approvedUnitSearchResults.splice(0);
        this.selectedDays = {};
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public submittedChanges?: any;
    public async submit(): Promise<boolean> {
        this.submittedChanges = null;
        const medication = this.buildSaveObject();

        // If Pending Change, push medication changes to Pending Changes array instead
        if(this.options.pendMedicationChanges) {
            if(!this.pendingMedicationChanges) this.pendingMedicationChanges = [];
            if(!medication.auth?.id && this.auth?.id) medication.auth = { id: this.auth.id };
            this.pendingMedicationChanges.push({ medication, type: ActionOptions.Update });
            return true;
        }
        else {
            return await this.saveMedication(medication);
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async searchUnits(value: string): Promise<Core.UnitOfMeasure[]> {
        const options: Partial<IQueryLoadOptions> = { top: 30 };
        const includes: string[] = [];

        const response = await this.lookupService.searchUnits(value, options, includes);
        return response.results;
    }

    @apiCall
    protected async saveMedication(medication: UM.AuthMedication): Promise<boolean> {
        if(!medication) { throw new Error('Unable to build save object'); }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.updateAuthMedication(medication);
            isSuccessful = response.isSuccessful;
            if(isSuccessful && this.medication) {
                this.submittedChanges = { medication: deepClone(this.medication) };
                this.reset();
                return true;
            }
            else {
                this.logger.error(response.resultText || 'unknown error');
                return false;
            }
        } catch(error) {
            throw new error;
        } finally {
            this.toaster.standard.saved('um:medication.$long', isSuccessful);
            this.state.endSave();
        }
    }
}
